"use client";
import React, { useEffect, useState } from "react";
import "~/styles/form.css";
import { X, Shield, ExternalLink } from "lucide-react";
import { analytics } from "lib/segment";
import { useRouter } from "next/navigation";

declare global {
  interface Window {
    lintrk: any;
  }
}

const HubspotContactForm = ({
  region,
  portalId,
  formId,
  conversion_id,
  segmentMsg = "Newsletter subscription submitted",
}) => {
  const [noticeIsVisible, setNoticeIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const router = useRouter();

  const handleDismiss = () => {
    setIsDismissed(true);
    setNoticeIsVisible(false);
  };

  useEffect(() => {
    // Check if user is on Firefox
    const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

    if (isFirefox) {
      setNoticeIsVisible(true);
    }
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.hsforms.net/forms/embed/v2.js";
    document.body.appendChild(script);

    script.addEventListener("load", () => {
      // @ts-ignore
      if (window.hbspt) {
        setNoticeIsVisible(false);

        // @ts-ignore
        window.hbspt.forms.create({
          target: `#hubspotForm-${formId}`,
          onFormSubmit: () => {
            if (conversion_id) {
              window.lintrk("track", { conversion_id: conversion_id });
            }
          },
          onFormSubmitted: (_, data) => {
            // if formId is 7f3330bd-39c5-4358-b56d-44b90ae62e8e, redirect to thank you page
            if (formId == "7f3330bd-39c5-4358-b56d-44b90ae62e8e") {
              router.push("/outshift-newsletter/thank-you");
            }
            const submissionValues = data.submissionValues;

            if (
              typeof window !== "undefined" &&
              (window.OnetrustActiveGroups == undefined ||
                window.OnetrustActiveGroups?.includes("2"))
            ) {
              // Segment Identify Call
              analytics.identify({
                firstname: submissionValues.firstname,
                lastname: submissionValues.lastname,
                email: submissionValues.email,
                jobtitle: submissionValues.jobtitle,
                agreed_to_pii:
                  submissionValues.consent_marketing_emails_and_privacy_policy,
              });

              // Segment Track Call
              analytics.track(segmentMsg, {
                firstname: submissionValues.firstname,
                lastname: submissionValues.lastname,
                email: submissionValues.email,
                jobtitle: submissionValues.jobtitle,
                agreed_to_pii:
                  submissionValues.consent_marketing_emails_and_privacy_policy,
              });
            }
          },
          ...{ region: region, portalId: portalId, formId: formId },
        });
      }
      setTimeout(() => {
        const formElement = document.querySelector(`#hubspotForm-${formId}`);
        if (formElement) {
          // Remove invalid role attributes
          const invalidRoles =
            formElement.querySelectorAll('[role="checkbox"]');
          invalidRoles.forEach((el) => el.removeAttribute("role"));
        }
      }, 500);
    });
  }, [region, portalId, formId]);

  return (
    <>
      {noticeIsVisible && !isDismissed ? (
        <div className="border-orange-200 z-50 mt-5 max-w-md rounded-lg border bg-white p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <Shield className="text-orange-500 h-5 w-5" />
            </div>

            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">
                Form Blocked by Firefox
              </h3>
              <p className="mb-3 text-sm text-gray-600">
                Firefox's Enhanced Tracking Protection may be blocking this
                form. To submit the form, please temporarily disable tracking
                protection for this site.
              </p>

              <div className="space-y-1 text-xs text-gray-500">
                <p>
                  <strong>To disable:</strong>
                </p>
                <p>1. Click the shield icon in your address bar</p>
                <p>2. Toggle off "Enhanced Tracking Protection"</p>
                <p>3. Refresh the page</p>
              </div>

              <a
                href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                Learn more about Firefox tracking protection
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600"
              aria-label="Dismiss notice"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div id={`hubspotForm-${formId}`}></div>
      )}
    </>
  );
};

export default HubspotContactForm;
